//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { RefObject, useEffect, useState } from 'react';

interface IContentRect {
  width: number;
  height: number;
}

const DEFAULT_CONTENT_RECT: IContentRect = {
  width: 0,
  height: 0,
};

export const useResizeObserver = (
  ref?: RefObject<HTMLElement>,
): IContentRect => {
  const [entry, setEntry] = useState<IContentRect>(DEFAULT_CONTENT_RECT);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([currentEntry]) => {
      setEntry(currentEntry?.contentRect ?? DEFAULT_CONTENT_RECT);
    });

    // This protects against the reference component being unrendered and resets
    // to default content rect if it isn't already set
    if (ref?.current) {
      resizeObserver.observe(ref.current);
    } else if (
      entry.width !== DEFAULT_CONTENT_RECT.width ||
      entry.height !== DEFAULT_CONTENT_RECT.height
    ) {
      setEntry(DEFAULT_CONTENT_RECT);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref?.current]);

  return entry;
};
